<template>
  <ProgressModal
    v-if="isLoadingAction"
    title="Please wait..."
    :progressIndicator="progressIndicator"
    :processedItems="processedItems"
    :totalItems="campaignsSelected.length"
  />

  <div
    v-if="!campaign_id"
    id="campaigns-table"
    :class="`content-wrapper animation-slide ${
      isMenuOpen ? 'menu-open' : 'menu-closed'
    }`"
  >
    <div class="content content-styles text-white ov-visible md:mx-[1rem]">
      <div class="box text-white large content-box-style ov-hidd p-0">
        <Filters
          :options="options"
          :showActionButton="true"
          :search="search"
          :resetFilter="resetFilter"
          :resetPagination="resetPagination"
        >
          <router-link
            :to="{
              name: 'ChallengesCreate',
              params: {
                lang: $route.params.lang || 'en',
                ...($route.params.skinFilt || skinStorage
                  ? {
                      skinFilt: (
                        $route.params.skinFilt || skinStorage
                      ).toString(),
                    }
                  : {}),
              },
            }"
            class="text-teal-900 mx-2 bg-teal-100 hover:bg-teal-200 inline-flex items-center gap-2 rounded-xl px-4 py-2 transition-colors duration-150 active:scale-95 border border-teal-200"
          >
            <i class="fa-solid fa-award text-lg text-teal-600"></i>
            <span class="text">{{ $t("util.create") }}</span>
          </router-link>
        </Filters>

        <Transition name="fade-slide">
          <div
            v-if="campaignsSelected.length > 0"
            class="manage-freebets-status mt-3"
          >
            <div class="flex items-center gap-2">
              <button
                v-for="(btn, index) in rainsButton"
                :key="index"
                @click="btn.action()"
                :class="[
                  'inline-flex items-center gap-2 px-4 py-1.5 rounded-full font-bold text-sm transition-all duration-200 active:scale-95 shadow-sm whitespace-nowrap',
                  btn.name === 'Enable'
                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200'
                    : '',
                  btn.name === 'Disable'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-200'
                    : '',
                  btn.name === 'Remove'
                    ? 'bg-rose-100 text-rose-700 hover:bg-rose-200 border border-rose-200'
                    : '',
                ]"
              >
                <span>{{ btn.name }}</span>
                <span class="opacity-60 text-xs"
                  >({{ campaignsSelected.length }})</span
                >
                <i :class="`fas fa-${btn.icon} text-xs`"></i>
              </button>
            </div>
          </div>
        </Transition>

        <div
          v-if="isLoading"
          class="flex-grow flex flex-col items-center justify-center p-20"
        >
          <Loader />
        </div>

        <div
          v-else
          class="table-container-custom shadow-sm rounded-2xl overflow-hidden border border-1 mb-3 mt-3"
        >
          <DataTable
            v-model:selection="campaignsSelected"
            :value="tableData"
            :loading="isLoading"
            :lazy="true"
            :paginator="true"
            :rows="selectedLimit"
            :totalRecords="totalElements"
            :first="(currentPage - 1) * selectedLimit"
            dataKey="_id"
            @page="onPage"
            @sort="onSort"
            :sortField="sortField"
            :sortOrder="sortOrder === 'desc' ? -1 : 1"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            :rowsPerPageOptions="[15, 25, 50, 100]"
            :currentPageReportTemplate="`Showing {first} to {last} of {totalRecords} challenges`"
            showCurrentPageReport
            stripedRows
            removableSort
            scrollable
            scrollHeight="flex"
            class="freebets-datatable p-datatable-sm"
          >
            <template #empty>
              <div class="text-center py-4 text-muted">
                No challenges found.
              </div>
            </template>
            <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>

            <Column
              v-for="col in base_columns"
              :key="col.slug"
              :field="col.slug"
              :header="col.label || col.slug"
              :sortable="true"
            >
              <template #body="{ data, field }">
                <div
                  v-if="typeof data[field] === 'boolean'"
                  class="text-center justify-start flex gap-2"
                >
                  <span
                    :class="data.is_enabled ? 'text-success' : 'text-danger'"
                  >
                    <i
                      :class="`fas fa-${
                        data.is_enabled ? 'check-circle' : 'times-circle'
                      }`"
                    ></i>
                    {{ data.is_enabled ? "Enabled" : "Disabled" }}
                  </span>
                </div>

                <span v-else-if="['date', 'time'].includes(col.type)">
                  {{ formatDate(data[field]) }}
                </span>

                <span v-else>{{ data[field] }}</span>
              </template>
            </Column>

            <Column
              header="Actions"
              headerStyle="min-width: 10rem"
              :exportable="false"
            >
              <template #body="slotProps">
                <div class="flex items-center gap-1">
                  <router-link
                    :to="{
                      name: 'Challenges',
                      params: {
                        id: slotProps.data._id,
                        lang: $route.params.lang || 'en',
                        ...($route.params.skinFilt || skinStorage || slotProps.data.skin_id
                          ? {
                              skinFilt: (
                                $route.params.skinFilt ||
                                skinStorage ||
                                slotProps.data.skin_id
                              ).toString(),
                            }
                          : {}),
                      },
                    }"
                  >
                    <Button
                      icon="pi pi-eye"
                      rounded
                      text
                      severity="info"
                      v-tooltip.top="'View Detail'"
                      class="hover:bg-blue-50 rounded-full"
                    />
                  </router-link>

                  <router-link
                    :to="{
                      name: 'ChallengesEdit',
                      params: {
                        id: slotProps.data._id,
                        lang: $route.params.lang || 'en',
                        ...($route.params.skinFilt || skinStorage || slotProps.data.skin_id
                          ? {
                              skinFilt: (
                                $route.params.skinFilt ||
                                skinStorage ||
                                slotProps.data.skin_id
                              ).toString(),
                            }
                          : {}),
                      },
                    }"
                  >
                    <Button
                      icon="pi pi-pencil"
                      rounded
                      text
                      severity="warning"
                      v-tooltip.top="'Edit Freebet'"
                      class="hover:bg-amber-50 rounded-full"
                  /></router-link>

                  <Button
                    icon="pi pi-trash"
                    rounded
                    text
                    severity="danger"
                    v-tooltip.top="'Remove Freebet'"
                    @click="() => removeRainRow(slotProps.data)"
                    class="hover:bg-red-50 rounded-full"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>
  </div>

  <div
    id="freebet-table-details"
    class="h-auto"
    v-else
    :class="`content-wrapper ${isMenuOpen ? 'menu-open' : 'menu-closed'}`"
  >
    <div class="flex items-center justify-start mb-3 md:px-3">
      <div
        class="inline-flex p-1.5 bg-gray-100/80 rounded-2xl border border-gray-200/50 shadow-inner backdrop-blur-md overflow-x-auto"
      >
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :id="`tab-${tab.value}`"
          @click="tabSelected = tab.value"
          :class="[
            'relative px-3 py-2.5 text-[11px] whitespace-nowrap font-semibold uppercase tracking-widest transition-all duration-300 rounded-xl flex items-center gap-2 group',
            tabSelected === tab.value
              ? 'bg-white text-[#386f99] shadow-[0_4px_12px_rgba(0,0,0,0.05)] ring-1 ring-black/[0.03]'
              : 'text-slate-500 hover:text-slate-700 hover:bg-white/50',
          ]"
        >
          <div class="relative flex items-center justify-center">
            <span
              v-if="tabSelected === tab.value"
              class="absolute h-2 w-2 animate-pulse rounded-full bg-[#386f99]/30"
            ></span>
            <span
              :class="[
                'relative rounded-full transition-all duration-300',
                tabSelected === tab.value
                  ? 'h-1.5 w-1.5 bg-[#386f99] scale-110'
                  : 'h-1 w-1 bg-gray-300 group-hover:bg-gray-400',
              ]"
            ></span>
          </div>

          <span class="relative">{{ tab.label }}</span>
        </button>
      </div>
    </div>

    <ChallengeInfo
      v-if="tabSelected === 'challenge_details'"
      :isLoadingDetail="isLoading"
      :detail="detail"
      :campaignId="campaign_id"
      :saveDetail="saveDetail"
    />

    <RainPlayerActive
      v-if="tabSelected === 'players_awarded'"
      :campaignId="campaign_id"
      :skinFilt="challenge_skin_id"
    />

    <ChallengePlayerActive
      v-if="tabSelected === 'players_active'"
      :campaignId="campaign_id"
      :skinFilt="challenge_skin_id"
      :challengeMode="detail?.mode"
    />

    <RoundFinder
      :skinIdProps="challenge_skin_id"
      :fieldToExclude="['campaign_id']"
      :options="filters"
      v-if="tabSelected === 'rounds_info'"
    />

    <RainRestrictions
      :freebet_id="campaign_id"
      :skinFilt="challenge_skin_id"
      :detail="detail"
      v-if="tabSelected === 'campaign_restricted'"
    />
  </div>
</template>

<script>
// API & Store
import { getFetch, putFetch, deleteRequest } from "@/api/axiosClient";
import { mapGetters } from "vuex";
import { formatString } from "@/helpers/date";
import Swal from "sweetalert2";

// PrimeVue
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { Button } from "primevue";

// Componenti Locali
import Loader from "../components/Loader.vue";
import Filters from "../components/table/Filters.vue";
import ProgressModal from "../components/Modal/ProgressModal.vue";
import ChallengeInfo from "../components/challenges/ChallengeInfo.vue";
import RainPlayerActive from "../components/RainComponent/RainPlayerActive.vue";
import ChallengePlayerActive from "../components/challenges/ChallengePlayerActive.vue";
import GeneratedRain from "../components/RainComponent/GeneratedRain.vue";
import RainRestrictions from "../components/RainComponent/RainRestrictions.vue";
import RoundFinder from "../components/round/RoundFinder.vue";

export default {
  name: "CampaignPage",
  components: {
    DataTable,
    Column,
    ProgressModal,
    Loader,
    Filters,
    ChallengeInfo,
    RainPlayerActive,
    ChallengePlayerActive,
    RoundFinder,
    GeneratedRain,
    Button,
    RainRestrictions,
  },
  data() {
    return {
      tableData: [],
      campaignsSelected: [],
      isLoading: false,
      isLoadingAction: false,
      totalElements: 0,
      currentPage: 1,
      selectedLimit: 50,
      sortField: "created_at",
      sortOrder: "desc",

      filters: [
        {
          label: "Round ID",
          type: "text",
          placeholder: "Search for Round ID",
          property: "round_id",
        },
        {
          label: "Player ID",
          type: "text",
          placeholder: "Search for Player ID",
          property: "player_id",
        },
        {
          label: "Bet Type",
          type: "select",
          placeholder: "Choose Bet Type",
          property: "type_id",
          options: ["All", "Cashout 1", "Cashout 2", "Even/Odd", "Ranges"],
        },
        {
          label: "Bet ID",
          type: "text",
          placeholder: "Search for Bet ID",
          property: "bet_id",
        },
        {
          label: "Campaign ID",
          type: "text",
          property: "campaign_id",
          disabled: true,
          default: "",
        },
        {
          label: "Freebet ID",
          type: "text",
          placeholder: "Search for Freebet ID",
          property: "freebet_id",
        },
      ],
      rainsButton: [
        {
          name: "Enable",
          label: "enable",
          icon: "circle-check",
          action: () => this.changeStatus("enable"),
          btnType: "primary",
        },
        {
          name: "Disable",
          label: "disable",
          icon: "circle-xmark",
          action: () => this.changeStatus("disable"),
          btnType: "warning",
        },
        {
          name: "Remove",
          label: "remove",
          icon: "trash",
          action: () => this.removeMassive(),
          btnType: "danger",
        },
      ],

      challenge_skin_id: "",
      detail: {},
      tabSelected: "challenge_details",
      tabs: [
        { label: "Detail", value: "challenge_details" },
        { label: "Players Active", value: "players_active" },
        { label: "Players Awarded", value: "players_awarded" },
        { label: "Rounds", value: "rounds_info" },
        { label: "Players Restricted", value: "campaign_restricted" },
      ],

      base_columns: [
        { slug: "_id", label: "ID", type: "text" },
        { slug: "title", type: "text", label: "Title" },
        { slug: "skin_id", type: "text", label: "Skin" },
        { slug: "is_enabled", type: "boolean", label: "Enabled" },
        { slug: "quantity", type: "text", label: "Qty" },
        { slug: "type", type: "text", label: "Type" },
        { slug: "mode", type: "text", label: "Mode" },
        { slug: "start_date", label: "Start Date", type: "date" },
        { slug: "end_date", label: "End Date", type: "date" },
        { slug: "created_at", label: "Created At", type: "date" },
      ],

      options: [
        { label: "ID", type: "text", placeholder: "Id", property: "_id" },
        {
          label: "Start Date",
          type: "datetime-local",
          property: "start_date",
          isDateTime: true,
        },
        {
          label: "End Date",
          type: "datetime-local",
          property: "end_date",
          isDateTime: true,
        },
        {
          label: "Mode",
          type: "select",
          placeholder: "Choose Mode",
          property: "campaign_mode",
          options: ["All", "Mission", "Race", "Tournament"],
        },
        {
          label: "Enabled",
          type: "select",
          property: "is_enabled",
          options: ["All", "True", "False"],
        },
      ],

      progressIndicator: 0,
      processedItems: 0,
      skinListAdmin: "All",
    };
  },
  computed: {
    ...mapGetters(["isMenuOpen", "allowedSkins", "isAdmin"]),
    campaign_id() {
      return this.$route.params.id;
    },
  },
  watch: {
    campaign_id(newVal) {
      newVal ? this.fetchChallengeDetails() : this.search();
    },
    allowedSkins(newVal) {
      if (Array.isArray(newVal)) this.showSkinList();
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return "-";

      const date = new Date(value);

      if (isNaN(date.getTime())) return value;

      return date.toLocaleString();
    },
    onPage(event) {
      this.currentPage = event.page + 1;
      this.selectedLimit = event.rows;
      this.search();
    },
    onSort(event) {
      this.sortField = event.sortField || "created_at";
      this.sortOrder = event.sortOrder === 1 ? "asc" : "desc";
      this.search();
    },

    async search() {
      const params = {
        page: this.currentPage,
        limit: this.selectedLimit,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        mode: "challenge",
      };

      if (this._id) params.id = this._id.trim();
      if (this.start_date) params.start_date = new Date(this.start_date + "Z");
      if (this.end_date) params.end_date = new Date(this.end_date + "Z");

      if (this.campaign_mode && this.campaign_mode !== "All") {
        params.mode = this.campaign_mode.toLowerCase();
      }
      if (this.is_enabled && this.is_enabled !== "All") {
        params.is_enabled = this.is_enabled === "True";
      }

      // Gestione Skin Admin
      const parsedSkinId = parseInt(this.skinListAdmin);
      if (this.isAdmin && !isNaN(parsedSkinId)) {
        params.skin_id = parsedSkinId;
      }

      try {
        this.isLoading = true;
        const response = await getFetch(
          "/campaigns",
          params,
          this.skinListAdmin === "All",
        );
        this.tableData = response.data || [];
        this.totalElements = response.totalItems || 0;
      } catch (error) {
        console.error("Search Error:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async resetFilter() {
      this.currentPage = 1;
      this._id = "";
      this.start_date = this.getInitialTimeStart();
      this.end_date = null;
      this.campaign_mode = "All";
      this.is_enabled = "All";
      if (this.isAdmin) this.skinListAdmin = "All";
      await this.search();
    },

    async removeRainRow(row) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        try {
          this.isLoading = true;
          await deleteRequest(`campaigns/${row._id}`);
          Swal.fire("Deleted!", "The campaign has been deleted.", "success");
          this.search();
        } catch (error) {
          Swal.fire("Error", "Failed to delete.", "error");
        } finally {
          this.isLoading = false;
        }
      }
    },

    async changeStatus(action) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: `Do you want to ${action} ${this.campaignsSelected.length} campaigns?`,
        icon: "question",
        showCancelButton: true,
      });

      if (result.isConfirmed) {
        this.isLoadingAction = true;
        try {
          for (let i = 0; i < this.campaignsSelected.length; i++) {
            const item = this.campaignsSelected[i];
            await putFetch(`/campaigns/${item._id}`, {
              is_enabled: action === "enable",
            });
            this.processedItems = i + 1;
            this.progressIndicator = Math.round(
              ((i + 1) / this.campaignsSelected.length) * 100,
            );
          }
          await Swal.fire("Complete", "Elements updated!", "success");
          this.campaignsSelected = [];
          this.search();
        } finally {
          this.isLoadingAction = false;
        }
      }
    },

    async removeMassive() {
      const result = await Swal.fire({
        title: "Delete All Selected?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete them!",
      });

      if (result.isConfirmed) {
        this.isLoadingAction = true;
        try {
          for (let i = 0; i < this.campaignsSelected.length; i++) {
            await deleteRequest(`campaigns/${this.campaignsSelected[i]._id}`);
            this.processedItems = i + 1;
            this.progressIndicator = Math.round(
              ((i + 1) / this.campaignsSelected.length) * 100,
            );
          }
          this.campaignsSelected = [];
          this.search();
        } finally {
          this.isLoadingAction = false;
        }
      }
    },

    async fetchChallengeDetails() {
      this.isLoading = true;
      try {
        const data = await getFetch(`campaigns/${this.campaign_id}`);
        if (data) {
          this.detail = data;
          this.challenge_skin_id = data.skin_id;
          const playerTab = this.tabs.find((t) => t.value === "players_active");
          if (playerTab)
            playerTab.label =
              data.mode === "tournament" ? "Ranking" : "Players Active";
        }
      } catch (error) {
        this.$router.push("/404");
      } finally {
        this.isLoading = false;
      }
    },
    saveDetail(detailsProp) {
      this.challenge_skin_id = detailsProp.skin_id;
      this.detail = detailsProp;
    },
    getInitialTimeStart() {
      let date = new Date();
      date.setDate(date.getDate() - 7);
      date.setHours(0, 0, 0, 0);
      return formatString(date.toString(), { hours: 0, minutes: 0 });
    },
    showSkinList() {
      if (this.isAdmin) {
        const skinIdList = this.allowedSkins.map((s) => s.ref_id);
        const skinFilter = this.options.find(
          (o) => o.property === "skinListAdmin",
        );
        const options = ["All", ...skinIdList];
        if (!skinFilter) {
          this.options.push({
            label: "Skin ID",
            type: "select",
            property: "skinListAdmin",
            options,
          });
        } else {
          skinFilter.options = options;
        }
      }
    },
  },
  async mounted() {
    this.showSkinList();
    if (this.campaign_id) {
      await this.fetchChallengeDetails();
    } else {
      this.start_date = this.getInitialTimeStart();
      await this.search();
    }
  },
};
</script>

<style scoped></style>
